import { ClipboardList, FileText, MapPin, PackageCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import api, { getError } from "@/api";
import { PdfFileActions } from "@/components/PdfFileActions";

const movementLabels = {
  entree: "Entrée",
  sortie: "Sortie",
  transfert: "Transfert",
  initial: "Stock initial",
};

const displayDate = (value) => value
  ? new Date(value).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })
  : "—";

export default function WarehouseDocumentsPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const loadWarehouses = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/warehouse-documents");
      setWarehouses(response.data);
      setWarehouseId((current) => current || response.data[0]?.warehouse_id || "");
      setError("");
    } catch (requestError) {
      setError(getError(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    if (!warehouseId) {
      setSummary(null);
      return;
    }
    try {
      const response = await api.get(`/warehouse-documents/${warehouseId}/last-movement`);
      setSummary(response.data);
      setError("");
    } catch (requestError) {
      setError(getError(requestError));
    }
  }, [warehouseId]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const selectedWarehouse = warehouses.find((warehouse) => warehouse.warehouse_id === warehouseId);
  const getStockPdfBlob = async () => {
    const response = await api.get(`/warehouse-documents/${warehouseId}/stock-state/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };
  const getMovementPdfBlob = async () => {
    const response = await api.get(`/warehouse-documents/${warehouseId}/last-movement/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

  return (
    <div className="page-enter warehouse-documents-page" data-testid="warehouse-documents-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">DOCUMENTS A4</p>
          <h1 data-testid="warehouse-documents-title">Documents de dépôt</h1>
          <p data-testid="warehouse-documents-description">
            Consultez, imprimez, téléchargez ou partagez les états sécurisés de votre dépôt.
          </p>
        </div>
      </div>
      {error && <div className="notice-error" data-testid="warehouse-documents-error">{error}</div>}
      <section className="warehouse-document-selector-band" data-testid="warehouse-document-selector-band">
        <div>
          <p className="eyebrow">DÉPÔT CONCERNÉ</p>
          <strong data-testid="warehouse-document-selected-name">
            {selectedWarehouse?.name || "Aucun dépôt accessible"}
          </strong>
        </div>
        <label>
          <span>Choisir un dépôt</span>
          <select
            data-testid="warehouse-document-selector"
            disabled={!warehouses.length}
            onChange={(event) => setWarehouseId(event.target.value)}
            value={warehouseId}
          >
            {!warehouses.length && <option value="">Aucun dépôt</option>}
            {warehouses.map((warehouse) => (
              <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                {warehouse.name} · {warehouse.subzone}
              </option>
            ))}
          </select>
        </label>
      </section>
      {isLoading && <div className="loading-line" data-testid="warehouse-documents-loading">Chargement…</div>}
      {!isLoading && !warehouses.length && (
        <div className="empty-inline" data-testid="warehouse-documents-empty">
          Aucun dépôt n’est disponible pour vos documents.
        </div>
      )}
      {warehouseId && (
        <div className="warehouse-document-layout" data-testid="warehouse-document-layout">
          <section className="warehouse-document-card" data-testid="warehouse-stock-document-card">
            <div className="warehouse-document-card-heading">
              <div>
                <p className="eyebrow">INVENTAIRE</p>
                <h2>État de stock</h2>
              </div>
              <PackageCheck aria-hidden="true" size={22} />
            </div>
            <p>
              Produit, quantité disponible, seuil d’alerte et statut dans un document A4 paginé.
            </p>
            <PdfFileActions
              fileName={`etat-stock-${warehouseId}.pdf`}
              getPdfBlob={getStockPdfBlob}
              testIdPrefix="warehouse-state"
            />
          </section>
          <section className="warehouse-document-card" data-testid="warehouse-last-movement-card">
            <div className="warehouse-document-card-heading">
              <div>
                <p className="eyebrow">DERNIÈRE OPÉRATION</p>
                <h2>Résumé du dernier mouvement</h2>
              </div>
              <ClipboardList aria-hidden="true" size={22} />
            </div>
            <p>
              Entrée, sortie ou transfert avec les quantités déplacées et le stock restant du dépôt.
            </p>
            <PdfFileActions
              fileName={`dernier-mouvement-${warehouseId}.pdf`}
              getPdfBlob={getMovementPdfBlob}
              testIdPrefix="warehouse-last-movement"
            />
          </section>
        </div>
      )}
      {warehouseId && summary && (
        <section className="last-movement-summary" data-testid="last-movement-summary">
          <div className="last-movement-summary-heading">
            <div>
              <p className="eyebrow">APERÇU OPÉRATIONNEL</p>
              <h2>Dernier mouvement de {selectedWarehouse?.name}</h2>
            </div>
            <FileText aria-hidden="true" size={21} />
          </div>
          {!summary.movement && (
            <p data-testid="last-movement-empty">Aucun mouvement ne concerne encore ce dépôt.</p>
          )}
          {summary.movement && (
            <>
              <div className="last-movement-metadata" data-testid="last-movement-metadata">
                <span><MapPin aria-hidden="true" size={15} /> {summary.movement.source_name}</span>
                <span>{movementLabels[summary.movement.movement_type] || "Mouvement"}</span>
                <span>{displayDate(summary.movement.movement_date || summary.movement.created_at)}</span>
              </div>
              <div className="last-movement-lines" data-testid="last-movement-lines">
                {summary.lines.map((line) => (
                  <div data-testid={`last-movement-line-${line.product_id}`} key={line.product_id}>
                    <strong>{line.product_name}</strong>
                    <span>Quantité : {line.quantity}</span>
                    <b>Stock restant : {line.remaining_quantity}</b>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}